import { AfterViewInit, Component, ElementRef, OnDestroy, Renderer2, ViewChild, inject } from '@angular/core';
import { BehaviorSubject, combineLatest, from, mergeMap, Observable, of, Subject, timer, toArray } from 'rxjs';
import {
  Avatar,
  DATA_SELECTOR_INJECTION_TOKEN,
  getHubspotElement,
  ignoreNullish,
  isLoaded,
  LoadableArrayLoadStrategy,
  LoadableHelper,
  LoadableStatus
} from '@aiwyn/shared';
import {
  catchError,
  debounce,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  skip,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs/operators';
import { ClientRequestsLayoutService } from '../../services';
import { ClientRequestGroupsContainerComponent } from '../client-request-groups-container/client-request-groups-container.component';
import { ALL_DELIVERABLES, AllDeliverables, ClientRequestQueryParams, ClientRequestsLayout, PROJECT_LABEL } from '../../models';
import { Store } from '@ngxs/store';
import {
  ClearSelectedClientRequestGroupIdentity,
  ClearSelectedClientRequestId,
  ClearSelectedRequestAsModified,
  ClientRequestsState,
  InitializeClientRequests,
  SetFocusedDeliverableId,
  SetSelectedClientRequestGroupIdentity,
  SetSelectedClientRequestId,
} from '../../state';
import { CommonClientRequestFactoryService, CommonClientRequestsState, GetClientRequests, TypedIdentity } from '@aiwyn/common/client-requests';
import { ActivatedRoute, Router } from '@angular/router';
import { ClientRequestGroupType } from '@aiwyn/api';
import { ConfigService, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';

const INITIALIZATION_TRANSITION_DURATION = 500;
const SIDE_DRAWER_CLOSE_ANIMATION_DURATION = 250;

@Component({
  selector: 'app-client-requests-page',
  templateUrl: './client-requests-page.component.html',
  styleUrls: ['./client-requests-page.component.scss', '../../styles/client-requests.scss'],
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'client-requests-page',
    },
  ],
  standalone: false,
})
export class ClientRequestsPageComponent implements AfterViewInit, OnDestroy {
  private readonly _unsubscribe$ = new Subject<void>();
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  @ViewChild(ClientRequestGroupsContainerComponent) groupsContainer: ClientRequestGroupsContainerComponent;

  showUpsell$ = new BehaviorSubject<boolean>(true);
  isFullLayout$: Observable<boolean> = this.layoutService.layout$.pipe(
    map(layout => layout === ClientRequestsLayout.FULL),
    shareReplay(1),
  );
  manageGridLayout$ = this.isFullLayout$.pipe(
    tap(isFullLayout => {
      const action: keyof Renderer2 & ('removeClass' | 'addClass') = isFullLayout ? 'removeClass' : 'addClass';
      this.renderer[action](this.host.nativeElement, 'compact-layout');
    }),
    takeUntil(this._unsubscribe$),
  );

  setSelectionsFromQueryParamsOnLoad$ = combineLatest([
    this.store.select(ClientRequestsState.getSlices.status).pipe(isLoaded()),
    this.route.queryParams as Observable<ClientRequestQueryParams>,
    this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.CXP_ENABLE_REQUEST_LISTS_LOCKDOWN),
  ]).pipe(
    take(1),
    switchMap(([, { group, request }, lockdownEnabled]) => {
      if (!group && !request) {
        return of(undefined);
      }

      // If lockdown feature is enabled, check if the request belongs to a locked group
      if (lockdownEnabled && request) {
        const requestData = this.store.selectSnapshot(CommonClientRequestsState.getClientRequest(+request));
        const isLocked = (requestData?.projectLockdowns?.length ?? 0) > 0;
        if (isLocked) {
          return from(this.router.navigate([], {
            queryParams: { request: null, group: null },
            queryParamsHandling: 'merge',
            relativeTo: this.route,
          }));
        }
      }

      const standardizedGroup = group
        ?.toLowerCase()
        .replace(PROJECT_LABEL, ClientRequestGroupType.JOB) as TypedIdentity<ClientRequestGroupType>;

      return this.store.dispatch([
        ...(standardizedGroup ? [new SetSelectedClientRequestGroupIdentity(standardizedGroup)] : []),
        ...(request ? [new SetSelectedClientRequestId(+request)] : []),
      ]);
    }),
  );

  isInitializing$: Observable<boolean> = this.store.select(ClientRequestsState.getSlices.status).pipe(
    map(status => LoadableHelper.isLoading(status)),
    distinctUntilChanged(),
    shareReplay(1),
  );

  initializationTransitionComplete: boolean;
  manageInitializationTransition$ = this.isInitializing$.pipe(
    tap(isInitializing => this.renderer.setStyle(this.host.nativeElement, 'margin-top', isInitializing ? '100%' : '0')),
    debounce(isInitializing => isInitializing ? of(undefined) : timer(INITIALIZATION_TRANSITION_DURATION)),
    tap(isInitializing => {
      this.initializationTransitionComplete = !isInitializing;
    }),
    takeUntil(this._unsubscribe$),
  );

  isClientRequestGroupSelected$: Observable<boolean> = this.store.select(ClientRequestsState.getSlices.selectedGroupIdentity).pipe(
    map(Boolean),
    distinctUntilChanged(),
    shareReplay(1),
  );

  isClientRequestSelected$: Observable<boolean> = this.store.select(ClientRequestsState.getSlices.selectedRequestId).pipe(
    map(Boolean),
    distinctUntilChanged(),
    shareReplay(1),
  );

  manageHostStyleOnGroupSelection$ = this.isClientRequestGroupSelected$.pipe(
    tap(isClientRequestGroupSelected => {
      if (isClientRequestGroupSelected) {
        this.renderer.setStyle(this.host.nativeElement, 'padding-bottom', '0');
      } else {
        this.renderer.removeStyle(this.host.nativeElement, 'padding-bottom');
      }
    }),
    takeUntil(this._unsubscribe$),
  );

  selectedGroupAssignees$ = this.factoryService.getClientRequestGroups$(
    this.store.select(ClientRequestsState.getSlices.selectedGroupIdentity),
  ).pipe(
    switchMap(selectedGroup => selectedGroup ? selectedGroup.assignees$ : of(undefined)),
  );

  avatars$: Observable<Avatar[]> = this.selectedGroupAssignees$.pipe(
    switchMap(assignees =>
      assignees && assignees.length > 0
        ? from(assignees).pipe(
          mergeMap(assignee => assignee.data$),
          map(contact => {
            const fullName = contact.contactInfo.fullName;
            const firstName = fullName.split(' ')[0];
            const lastName = fullName.split(' ').pop();

            return {
              id: contact.contactInfo.id,
              firstName,
              lastName
            } as Avatar;
          }),
          toArray()
        )
        : of([])
    )
  );
  onGroupSelection$ = combineLatest([
    this.store.select(ClientRequestsState.getSlices.selectedGroupIdentity).pipe(
      skip(1),
      map(group => group?.replace(ClientRequestGroupType.JOB, PROJECT_LABEL)),
    ),
    this.route.queryParams.pipe(
      map<ClientRequestQueryParams, string>(({ group }) => group),
      map(group => group?.replace(ClientRequestGroupType.JOB, PROJECT_LABEL)),
    ),
  ]).pipe(
    filter(([selectedGroup, routeGroup]) => selectedGroup !== routeGroup),
    switchMap(([selectedGroup]) => this.router.navigate(
      [],
      {
        queryParams: {
          group: selectedGroup,
          deliverable: undefined, // ensure deliverable is cleared when group selection is manually changed
        },
        queryParamsHandling: 'merge',
        relativeTo: this.route,
      },
    )),
    takeUntil(this._unsubscribe$),
  );

  onRequestSelections$ = combineLatest([
    this.store.select(ClientRequestsState.getSlices.selectedRequestId).pipe(skip(1)),
    this.route.queryParams.pipe(
      map<ClientRequestQueryParams, number>(({ request }) => request ? +request : undefined),
    ),
  ]).pipe(
    filter(([selectedRequest, routeRequest]) => selectedRequest !== routeRequest),
    switchMap(([selectedRequest]) => this.router.navigate(
      [],
      {
        queryParams: { request: selectedRequest },
        queryParamsHandling: 'merge',
        relativeTo: this.route,
      },
    )),
    takeUntil(this._unsubscribe$),
  );

  setIsClientRequestDetailsOpen$ = this.store.select(ClientRequestsState.getSlices.selectedRequestId).pipe(
    map(Boolean),
    debounce(isClientRequestSelected => isClientRequestSelected ? of(undefined) : timer(SIDE_DRAWER_CLOSE_ANIMATION_DURATION)),
    tap(isClientRequestSelected => this.isClientRequestDetailsOpen$.next(isClientRequestSelected)),
    takeUntil(this._unsubscribe$),
  );

  setDeliverableToBeFocused$ = this.route.queryParams.pipe(
    take(1),
    filter(({ group }) => group), // group must be present for deliverable to be focused (may change in the future)
    map<ClientRequestQueryParams, number | AllDeliverables>(({ deliverable }) => {
      if (deliverable === ALL_DELIVERABLES) {
        return deliverable;
      }

      return Number.isNaN(+deliverable) ? undefined : +deliverable;
    }),
    ignoreNullish(),
    tap(deliverable => this.store.dispatch(new SetFocusedDeliverableId(deliverable))),
  );

  // If the chat widget (bottom left) hasn't loaded, addClass will throw an error. This catches it and retries 10 times over 5 seconds.
  manageHubspotElementVisibility$ = this.isClientRequestSelected$.pipe(
    distinctUntilChanged(),
    switchMap(isClientRequestSelected => timer(0, 500).pipe(
      tap(_ => {
        const action: keyof Renderer2 & ('removeClass' | 'addClass') = isClientRequestSelected ? 'addClass' : 'removeClass';
        this.renderer[action](getHubspotElement(), 'removed');
      }),
      catchError(_ => of(null)),
      // stop if it succeeds
      takeWhile(_ => {
        const hubspotElement = getHubspotElement();
        if (!hubspotElement) {
          return true;
        }

        const isHubspotElementRemoved = hubspotElement?.className?.includes('removed');
        return isClientRequestSelected ? !isHubspotElementRemoved : isHubspotElementRemoved;
      }),
      take(10),
    )),
    takeUntil(this._unsubscribe$),
  );

  constructor(
    public readonly layoutService: ClientRequestsLayoutService,
    public readonly factoryService: CommonClientRequestFactoryService,
    private readonly renderer: Renderer2,
    private readonly host: ElementRef<ClientRequestsPageComponent & HTMLElement>,
    private readonly store: Store,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly configService: ConfigService,
    private readonly launchDarklyService: LaunchDarklyService,
  ) {
    const clientRequestsStatus = this.store.selectSnapshot(ClientRequestsState.getSlices.status);
    if (LoadableHelper.shouldRequest(clientRequestsStatus)) {
      this.store.dispatch(new InitializeClientRequests());
    }

    this.setSelectionsFromQueryParamsOnLoad$.subscribe();

    this.onGroupSelection$.subscribe();
    this.onRequestSelections$.subscribe();
    this.setDeliverableToBeFocused$.subscribe();

    this.manageGridLayout$.subscribe();
    this.manageHostStyleOnGroupSelection$.subscribe();

    this.initializationTransitionComplete = this.store.selectSnapshot(ClientRequestsState.getSlices.status) === LoadableStatus.LOADED;
    this.manageInitializationTransition$.subscribe();

    this.setIsClientRequestDetailsOpen$.subscribe();
    this.manageHubspotElementVisibility$.subscribe();
    this.isClientRequestDetailsOpen$.subscribe();

    this.showUpsell$.next(this.configService.practiceOsEnabled);
  }

  isClientRequestDetailsOpen$ = new BehaviorSubject<boolean>(false);
  onClientRequestDetailsClosed(): void {
    const hasSelectedRequestBeenModified = this.store.selectSnapshot(ClientRequestsState.getSlices.hasSelectedRequestBeenModified);
    const selectedClientRequestId = this.store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId);

    this.store.dispatch([
      new ClearSelectedClientRequestId(),
      ...(
        hasSelectedRequestBeenModified
          ? [
            new ClearSelectedRequestAsModified(),
            new GetClientRequests([selectedClientRequestId], LoadableArrayLoadStrategy.REFRESH),
          ]
          : []
      ),
    ]);
  }

  ngAfterViewInit(): void {
    this.layoutService.startTransitionManager(
      this.host.nativeElement,
      this.isClientRequestGroupSelected$,
      this.renderer,
      {
        regular: {
          height: 'cxp-height-transition',
          width: 'cxp-width-transition',
          both: 'cxp-dimension-transition',
        },
        inverse: {
          height: 'cxp-height-transition-inverse',
          width: 'cxp-width-transition-inverse',
          both: 'cxp-dimension-transition-inverse',
        },
      },
    ).pipe(
      takeUntil(this._unsubscribe$),
    ).subscribe();
  }

  onGoBack(event: Event): void {
    (event.target as HTMLElement).blur();
    const selectedGroup = this.store.selectSnapshot(ClientRequestsState.getSlices.selectedGroupIdentity);
    this.store.dispatch(new ClearSelectedClientRequestGroupIdentity()).pipe(
      take(1),
      switchMap(() =>
        // scroll back to project
        this.store.select(ClientRequestsState.getSlices.selectedGroupIdentity).pipe(
          filter(group => !group), // Wait until group is cleared
          take(1),
          tap(() => {
            setTimeout(() => {
              this.groupsContainer.scrollToProject({ groupName: undefined, groupIdentity: selectedGroup});
            }, 250);
          })
        )
      ),
      takeUntil(this._unsubscribe$)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
